import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card } from '@/components/ui/Card';
import { TextField, PasswordField } from '@/components/ui/TextField';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Avatar } from '@/components/ui/Avatar';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { useAuth } from '@/auth/AuthProvider';
import { useUpdateProfile, useChangePassword } from '@/auth/hooks';
import { errorMessage } from '@/lib/errorMessage';
import { fileToAvatarDataUrl } from '@/lib/image';
import { MIN_PASSWORD_LENGTH } from '@/lib/config';
import { useT } from '@/i18n/useT';
import { rules } from '@/features/auth/rules';

interface ProfileForm {
  name: string;
  email: string;
}
interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirm: string;
}

export function AccountPage() {
  const { t } = useT();
  const { user } = useAuth();

  return (
    <>
      <Card title={t('account.title')} subtitle={t('account.subtitle')}>
        <AvatarSection />
        <ProfileSection key={user?.id} />
        <div className="divider" />
        <PasswordSection />
        <div className="meta-row">
          <span>
            {user
              ? t('account.memberSince', {
                  date: new Date(user.createdAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  }),
                })
              : ''}
          </span>
        </div>
      </Card>
    </>
  );
}

function AvatarSection() {
  const { t } = useT();
  const { user } = useAuth();
  const update = useUpdateProfile();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be re-picked later
    if (!file) return;
    setError('');
    setBusy(true);
    try {
      const dataUrl = await fileToAvatarDataUrl(file); // downscaled to ~256px in the browser
      await update.mutateAsync({ avatarUrl: dataUrl });
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      setError(
        code === 'too_large'
          ? t('account.photoTooLarge')
          : code === 'unsupported_type'
            ? t('account.photoUnsupported')
            : errorMessage(err)
      );
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    setError('');
    setBusy(true);
    try {
      await update.mutateAsync({ avatarUrl: null });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="avatar-section">
      <Avatar name={user.name} avatarUrl={user.avatarUrl} size={72} />
      <div className="avatar-actions">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          hidden
          onChange={onPick}
        />
        <div className="avatar-buttons">
          <Button variant="ghost" busy={busy} onClick={() => fileRef.current?.click()}>
            {t('account.changePhoto')}
          </Button>
          {user.avatarUrl && (
            <button type="button" className="btn-link" onClick={onRemove} disabled={busy}>
              {t('account.removePhoto')}
            </button>
          )}
        </div>
        <Alert kind="error">{error}</Alert>
      </div>
    </div>
  );
}

function ProfileSection() {
  const { t } = useT();
  const { user } = useAuth();
  const update = useUpdateProfile();
  const [saved, setSaved] = useState(false);
  const [reverify, setReverify] = useState(false);
  const [serverError, setServerError] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileForm>({
    defaultValues: { name: user?.name ?? '', email: user?.email ?? '' },
  });

  const onSubmit = handleSubmit(async (data) => {
    setSaved(false);
    setReverify(false);
    setServerError('');
    try {
      const r = await update.mutateAsync(data);
      // A changed email reverts the account to unverified; the server has sent a
      // fresh link to the new address that must be clicked before the next login.
      if (r.emailVerificationSent) setReverify(true);
      else setSaved(true);
    } catch (e) {
      setServerError(errorMessage(e));
    }
  });

  return (
    <>
      <h2 className="section-title">{t('account.profile')}</h2>
      {saved && <Alert kind="success">{t('account.profileSaved')}</Alert>}
      {reverify && <Alert kind="success">{t('account.emailReverify')}</Alert>}
      <Alert kind="error">{serverError}</Alert>
      <form onSubmit={onSubmit} noValidate>
        <TextField
          label={t('common.name')}
          autoComplete="name"
          error={errors.name?.message}
          disabled={update.isPending}
          {...register('name', rules.name)}
        />
        <TextField
          label={t('common.email')}
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          disabled={update.isPending}
          {...register('email', rules.email)}
        />
        <Button type="submit" busy={update.isPending}>
          {update.isPending ? t('common.saving') : t('common.save')}
        </Button>
      </form>
    </>
  );
}

function PasswordSection() {
  const { t } = useT();
  const change = useChangePassword();
  const [saved, setSaved] = useState(false);
  const [serverError, setServerError] = useState('');
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm<PasswordForm>();

  const onSubmit = handleSubmit(async (data) => {
    setSaved(false);
    setServerError('');
    try {
      await change.mutateAsync({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      reset();
      setSaved(true);
    } catch (e) {
      setServerError(errorMessage(e));
    }
  });

  return (
    <>
      <h2 className="section-title">{t('account.security')}</h2>
      {saved && <Alert kind="success">{t('account.passwordChanged')}</Alert>}
      <Alert kind="error">{serverError}</Alert>
      <form onSubmit={onSubmit} noValidate>
        <PasswordField
          label={t('account.currentPassword')}
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          disabled={change.isPending}
          {...register('currentPassword', rules.password)}
        />
        <PasswordField
          label={t('account.newPassword')}
          autoComplete="new-password"
          hint={t('signup.passwordHint', { min: MIN_PASSWORD_LENGTH })}
          error={errors.newPassword?.message}
          disabled={change.isPending}
          {...register('newPassword', {
            ...rules.newPassword,
            validate: (v) => v !== getValues('currentPassword') || t('account.passwordReused'),
          })}
        />
        <PasswordField
          label={t('account.confirmPassword')}
          autoComplete="new-password"
          error={errors.confirm?.message}
          disabled={change.isPending}
          {...register('confirm', {
            validate: (v) => v === getValues('newPassword') || t('reset.mismatch'),
          })}
        />
        <Button type="submit" variant="ghost" busy={change.isPending}>
          {change.isPending ? t('common.saving') : t('account.changePassword')}
        </Button>
      </form>
          <Card title={t('account.language')}>
        <p className="muted">{t('account.languageHint')}</p>
        <LanguageSwitcher />
      </Card>
</>
  );
}
